import {Component, ElementRef, Inject} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import { Currencies, DEFAULT_CURRENCY, FUNCTIONS, INDUSTRY, MAX_FULL_TIME_HOURS, ModelPreferences, ORGANISATION_NAME_MAX, ORGANISATION_NAME_MIN, Organisation, SUGGESTED_FULL_TIME_HOURS } from '../common/organisation';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DateAdapter, ErrorStateMatcher, MAT_DATE_FORMATS, MAT_NATIVE_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { UserService } from '../user/user.service';

export const GRI_DATE_FORMATS: MatDateFormats = {
  ...MAT_NATIVE_DATE_FORMATS,
  display: {
    ...MAT_NATIVE_DATE_FORMATS.display,
    dateInput: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    } as Intl.DateTimeFormatOptions,
  }
};

export class MyErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
      const isSubmitted = form && form.submitted;
      return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
    }
}
  
@Component({
    selector: 'organisation-dialog',
    templateUrl: 'organisation-dialog.html',
    styleUrls: ['organisation-dialog.css'],
    providers: [
        { provide: MAT_DATE_FORMATS, useValue: GRI_DATE_FORMATS },
    ],
    standalone: false
})
  export class OrganisationDialogComponent {
    organisation:Organisation
    isNew:boolean
    createdDate:string
    organisationForm: FormGroup
    matcher = new MyErrorStateMatcher();
    industries:any[]
    currencies = Currencies

    nameMin = ORGANISATION_NAME_MIN
    nameMax = ORGANISATION_NAME_MAX
    invoiceReferenceMax = 30
    billingEmailMax = 60
    contactIdMax = 50
    readonly suggestedHours = SUGGESTED_FULL_TIME_HOURS
    readonly maxHours = MAX_FULL_TIME_HOURS

    constructor(public dialogRef: MatDialogRef<OrganisationDialogComponent>, private formBuilder: FormBuilder, private userService:UserService,
        @Inject(MAT_DIALOG_DATA) public data: {organisation:Organisation, isNew?:boolean}, private readonly adapter: DateAdapter<Date>,
        private host:ElementRef<HTMLElement>, private snackBar:MatSnackBar) {

        this.adapter.setLocale("en-NZ");

        this.organisation = data.organisation
        this.isNew = !!data.isNew

        if (!this.organisation.modelPreferences) this.organisation.modelPreferences = {
            ignoreFunctions:[]
        } as ModelPreferences

        this.createdDate = new Date(this.organisation.createdDate).toLocaleDateString("en-NZ", { day: 'numeric', month: 'long', year: 'numeric' })
        if (!this.organisation.modelPreferences.ignoreFunctions) this.organisation.modelPreferences.ignoreFunctions = []
        this.organisationForm  =  this.formBuilder.group({
            name: [this.organisation.name, [Validators.required, Validators.minLength(ORGANISATION_NAME_MIN), Validators.maxLength(ORGANISATION_NAME_MAX)]],
            endDate: [{value:this.organisation.endDate, disabled:!this.isSystemAdminUser()}],
            industry: [this.organisation.industry, [Validators.required]],
            // billing already falls back to the default currency, so start from it rather than blocking the save
            currencyCode: [this.organisation.currencyCode || DEFAULT_CURRENCY, [Validators.required]],
            isPartner: [{value:this.organisation.isPartner, disabled:!this.isSystemAdminUser()}],
            isCustomer: [{value:!this.organisation.notCustomer, disabled:!this.isSystemAdminUser()}],
            invoiceReference: [this.organisation.invoiceReference, [Validators.maxLength(this.invoiceReferenceMax)]],
            billingEmail: [this.organisation.billingEmail, [Validators.maxLength(this.billingEmailMax), Validators.email]],
            contactId: [this.organisation.contactId, [Validators.maxLength(this.contactIdMax)]],
            isExemptFromPayment: [{value:this.organisation.isExemptFromPayment, disabled:!this.isSystemAdminUser()}],
            ignoreValue: [this.organisation.modelPreferences.ignoreFunctions.find(i => i === FUNCTIONS.VALUE)?true:false],
            ignoreCarbon: [this.organisation.modelPreferences.ignoreFunctions.find(i => i === FUNCTIONS.CARBON)?true:false],
            ignoreCost: [this.organisation.modelPreferences.ignoreFunctions.find(i => i === FUNCTIONS.COSTS)?true:false],
            ignoreCapacity: [this.organisation.modelPreferences.ignoreFunctions.find(i => i === FUNCTIONS.CAPACITY)?true:false],
            // a new organisation starts with a typical full-time year; one without it keeps capacity matching the work
            defaultCapacityHours: [this.organisation.defaultCapacityHours || (this.isNew ? SUGGESTED_FULL_TIME_HOURS : null), [Validators.min(1), Validators.max(MAX_FULL_TIME_HOURS)]],
        })
        this.industries = Object.values(INDUSTRY)
    }

    isSystemAdminUser() {
        return this.userService.isSysAdmin()
    }

    save() {
        this.organisationForm.markAllAsTouched()
        if (this.organisationForm.invalid) {
            // the dialog scrolls, so bring the first problem into view
            setTimeout(() => this.host.nativeElement.querySelector('.mat-form-field-invalid')?.scrollIntoView({ block: 'center', behavior: 'smooth' }))
            return
        }

        this.organisation.name = this.organisationForm.value.name
        if (this.isSystemAdminUser()) {
            this.organisation.endDate = this.organisationForm.value.endDate
            this.organisation.isPartner = this.organisationForm.value.isPartner
            this.organisation.notCustomer = !this.organisationForm.value.isCustomer
            this.organisation.isExemptFromPayment = this.organisationForm.value.isExemptFromPayment
            this.organisation.contactId = this.organisationForm.value.contactId
        }
        this.organisation.industry = this.organisationForm.value.industry
        this.organisation.currencyCode = this.organisationForm.value.currencyCode

        // a change to the full-time year changes everyone's capacity, so the insights are worked out again when it's saved
        const hours = Number(this.organisationForm.value.defaultCapacityHours) || 0
        if (!this.isNew && hours !== (this.organisation.defaultCapacityHours || 0)) {
            this.snackBar.open('Working out the insights again with the new full-time hours. This can take a minute.', 'close', {duration: 8000})
        }
        this.organisation.defaultCapacityHours = hours

        this.organisation.invoiceReference = this.organisationForm.value.invoiceReference
        this.organisation.billingEmail = this.organisationForm.value.billingEmail
        this.organisation.modelPreferences.ignoreFunctions = []
        if (this.organisationForm.value.ignoreValue) this.organisation.modelPreferences.ignoreFunctions.push(FUNCTIONS.VALUE)
        if (this.organisationForm.value.ignoreCarbon) this.organisation.modelPreferences.ignoreFunctions.push(FUNCTIONS.CARBON)
        if (this.organisationForm.value.ignoreCost) this.organisation.modelPreferences.ignoreFunctions.push(FUNCTIONS.COSTS)
        if (this.organisationForm.value.ignoreCapacity) this.organisation.modelPreferences.ignoreFunctions.push(FUNCTIONS.CAPACITY)

        this.dialogRef.close(this.organisation);
    }

    // fills in a typical full-time year
    useSuggestedHours() {
        this.organisationForm.controls['defaultCapacityHours'].setValue(SUGGESTED_FULL_TIME_HOURS)
    }

    onCancelClick(): void {
      this.dialogRef.close();
    }    
}